import { useState } from 'react';

interface ApiKeysTabProps {
  apiKey: string;
  webhookSecret: string;
}

function InfoBubble({ text }: { text: string }) {
  return (
    <span className="group relative inline-flex ml-1.5 cursor-help">
      <span
        className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] font-mono transition-colors"
        style={{ border: '1px solid var(--dash-active-border)', color: 'var(--dash-text-muted)' }}
      >
        ?
      </span>
      <span
        className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded px-3 py-2 font-sans text-[11px] leading-relaxed opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 shadow-xl z-50 pointer-events-none"
        style={{ background: 'var(--dash-overlay)', border: '1px solid var(--dash-border)', color: 'var(--dash-text-tertiary)' }}
      >
        {text}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent" style={{ borderTopColor: 'var(--dash-border)' }} />
      </span>
    </span>
  );
}

export default function ApiKeysTab({ apiKey, webhookSecret }: ApiKeysTabProps) {
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [secretVisible, setSecretVisible] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  const maskKey = (key: string) => key.slice(0, 8) + '...' + key.slice(-4);

  const copyToClipboard = (text: string, setter: (v: boolean) => void) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const actionBtnStyle = (id: string): React.CSSProperties => ({
    color: hoveredBtn === id ? 'var(--dash-text-tertiary)' : 'var(--dash-text-muted)',
    border: hoveredBtn === id ? '1px solid var(--dash-border)' : '1px solid var(--dash-border)',
  });

  return (
    <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
      <h2 className="font-mono text-[10px] uppercase tracking-widest mb-2" style={{ color: 'var(--dash-text-muted)' }}>API Keys</h2>
      <p className="font-sans text-[12px] leading-relaxed mb-5" style={{ color: 'var(--dash-text-muted)' }}>
        These keys authenticate your server-side requests to the BonesPay API. Keep them secure and never expose them in client-side code or public repositories.
      </p>

      <div className="space-y-5">
        <div className="rounded p-4" style={{ border: '1px solid var(--dash-border-light)', background: 'var(--dash-input)' }}>
          <div className="flex items-center mb-2">
            <label className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>API Key</label>
            <InfoBubble text="Your API Key is used to authenticate all requests to the BonesPay payment API. Include it in the X-API-Key header of every server-side request. This key identifies your merchant account and authorizes payment creation, order queries, and wallet operations." />
          </div>
          <p className="font-sans text-[11px] mb-3" style={{ color: 'var(--dash-text-faint)' }}>
            Used in the <code className="font-mono px-1 rounded" style={{ color: 'var(--dash-text-muted)', background: 'var(--dash-hover-bg)' }}>X-API-Key</code> header for all API requests. Required to create payments, query orders, and manage wallets.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs rounded px-3 py-2" style={{ color: 'var(--dash-text-muted)', background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}>
              {apiKeyVisible ? apiKey : maskKey(apiKey)}
            </code>
            <button
              onClick={() => setApiKeyVisible(!apiKeyVisible)}
              className="transition-colors p-1.5 font-mono text-[11px] rounded"
              style={actionBtnStyle('apiToggle')}
              onMouseEnter={() => setHoveredBtn('apiToggle')}
              onMouseLeave={() => setHoveredBtn(null)}
              title={apiKeyVisible ? 'Hide' : 'Reveal'}
            >
              {apiKeyVisible ? '\u25C9' : '\u25CB'}
            </button>
            <button
              onClick={() => copyToClipboard(apiKey, setApiKeyCopied)}
              className="transition-colors p-1.5 font-mono text-[11px] rounded"
              style={actionBtnStyle('apiCopy')}
              onMouseEnter={() => setHoveredBtn('apiCopy')}
              onMouseLeave={() => setHoveredBtn(null)}
              title="Copy"
            >
              {apiKeyCopied ? '\u2713' : '\u29C9'}
            </button>
          </div>
        </div>

        <div className="rounded p-4" style={{ border: '1px solid var(--dash-border-light)', background: 'var(--dash-input)' }}>
          <div className="flex items-center mb-2">
            <label className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>Webhook Secret</label>
            <InfoBubble text="Your Webhook Secret is used to verify that incoming webhook notifications are genuinely from BonesPay. When you receive a callback, compute an HMAC-SHA256 signature using this secret and compare it to the signature in the request header to prevent spoofing." />
          </div>
          <p className="font-sans text-[11px] mb-3" style={{ color: 'var(--dash-text-faint)' }}>
            Used to verify webhook signatures. Compute <code className="font-mono px-1 rounded" style={{ color: 'var(--dash-text-muted)', background: 'var(--dash-hover-bg)' }}>HMAC-SHA256</code> of the payload body with this secret and compare against the <code className="font-mono px-1 rounded" style={{ color: 'var(--dash-text-muted)', background: 'var(--dash-hover-bg)' }}>X-Webhook-Signature</code> header.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs rounded px-3 py-2" style={{ color: 'var(--dash-text-muted)', background: 'var(--dash-card)', border: '1px solid var(--dash-border)' }}>
              {secretVisible ? webhookSecret : maskKey(webhookSecret)}
            </code>
            <button
              onClick={() => setSecretVisible(!secretVisible)}
              className="transition-colors p-1.5 font-mono text-[11px] rounded"
              style={actionBtnStyle('secretToggle')}
              onMouseEnter={() => setHoveredBtn('secretToggle')}
              onMouseLeave={() => setHoveredBtn(null)}
              title={secretVisible ? 'Hide' : 'Reveal'}
            >
              {secretVisible ? '\u25C9' : '\u25CB'}
            </button>
            <button
              onClick={() => copyToClipboard(webhookSecret, setSecretCopied)}
              className="transition-colors p-1.5 font-mono text-[11px] rounded"
              style={actionBtnStyle('secretCopy')}
              onMouseEnter={() => setHoveredBtn('secretCopy')}
              onMouseLeave={() => setHoveredBtn(null)}
              title="Copy"
            >
              {secretCopied ? '\u2713' : '\u29C9'}
            </button>
          </div>
        </div>

        <div className="border border-amber-500/20 bg-amber-500/5 rounded p-4">
          <div className="flex items-start gap-2.5">
            <span className="text-amber-400 font-mono text-sm mt-0.5">&triangle;</span>
            <div>
              <p className="font-sans text-[12px] text-amber-300/90 font-medium mb-1">Security Notice</p>
              <p className="font-sans text-[11px] leading-relaxed" style={{ color: 'var(--dash-text-muted)' }}>
                Never share your API keys publicly. Store them as environment variables on your server. If you suspect a key has been compromised, regenerate it immediately from this panel. Rotating keys will invalidate the previous key and all active integrations will need to be updated.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
